import React from 'react';
import { motion } from 'framer-motion';

const CookiePolicy = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const contactBlock = (
    <div className="bg-[#F9FFF5] border border-[#2E7D32]/20 p-6 rounded-lg mt-2">
      <p><strong>Sam's Nutri Hub</strong></p>
      <p>Venkatesh Prime, 4th Floor, Burudgaon Road</p>
      <p>Ahmednagar, Maharashtra 414001</p>
      <p>Email: <a href="mailto:hello@samsnutrihub.com" className="text-[#2E7D32] hover:underline">hello@samsnutrihub.com</a></p>
      <p>Phone: <a href="tel:+919637375555" className="text-[#2E7D32] hover:underline">+91 96373 75555</a></p>
    </div>
  );

  return (
    <div className="bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div {...fadeInUp}>
          <p className="text-sm text-[#9E9E9E] mb-2">Last updated: November 1, 2023</p>
          <h1 className="text-4xl font-bold text-[#0B1320] font-poppins mb-10">
            Cookie Policy — Sam's Nutri Hub
          </h1>

          <div className="prose prose-lg max-w-none text-[#4A4A4A] leading-relaxed space-y-8">

            <section>
              <p>
                This Cookie Policy explains how Sam's Nutri Hub uses cookies and similar tracking technologies
                when you visit our website at{' '}
                <a href="https://samsnutrihub.com" target="_blank" rel="noopener noreferrer" className="text-[#2E7D32] hover:underline">
                  https://samsnutrihub.com
                </a>
                . By using our website, you consent to our use of cookies as described in this policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-3">1. What Are Cookies?</h2>
              <p>
                A cookie is a small text file that is downloaded onto your device when you visit a website.
                Cookies allow the website to recognise your device and remember certain information about your
                preferences or past actions — such as items in a shopping cart, language preferences, or
                whether you've visited before.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-3">2. How We Use Cookies</h2>
              <p>We use cookies to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Remember your preferences and settings across visits</li>
                <li>Understand how you interact with our website (pages visited, time spent, etc.)</li>
                <li>Improve the performance and functionality of our site</li>
                <li>Deliver relevant advertising based on your interests</li>
                <li>Track the effectiveness of our marketing campaigns</li>
              </ul>
              <p className="mt-4">
                We use both <strong>client-side cookies</strong> (set directly in your browser) and
                <strong> server-side tracking</strong> to get a more complete and privacy-respecting view
                of how our website is used.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-3">3. Third-Party Cookies</h2>
              <p>
                We use cookies from the following third-party advertising and analytics platforms:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>
                  <strong>Meta (Facebook &amp; Instagram)</strong> — for advertising, retargeting, and
                  measuring the performance of ads shown on Meta platforms
                </li>
                <li>
                  <strong>Google (Google Ads &amp; Google Analytics)</strong> — for understanding website
                  traffic, user behaviour, and the effectiveness of our Google advertising campaigns
                </li>
              </ul>
              <p className="mt-4">
                These third parties may set their own cookies on your device. We do not control these cookies
                and encourage you to review the respective privacy policies of Meta and Google for more
                information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-3">4. Types of Cookies We Use</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse border border-gray-200 mt-3">
                  <thead>
                    <tr className="bg-[#EAF8EC]">
                      <th className="border border-gray-200 px-4 py-2 text-left font-semibold text-[#0B1320]">Type</th>
                      <th className="border border-gray-200 px-4 py-2 text-left font-semibold text-[#0B1320]">Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-200 px-4 py-2">Essential</td>
                      <td className="border border-gray-200 px-4 py-2">Required for the website to function properly</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-200 px-4 py-2">Analytics</td>
                      <td className="border border-gray-200 px-4 py-2">Help us understand how visitors use our site (Google Analytics)</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-4 py-2">Advertising</td>
                      <td className="border border-gray-200 px-4 py-2">Used to show relevant ads (Meta Pixel, Google Ads)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-200 px-4 py-2">Preference</td>
                      <td className="border border-gray-200 px-4 py-2">Remember your settings and personalise your experience</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-3">5. Managing Cookies</h2>
              <p>
                You can control and manage cookies through your web browser settings. Most browsers allow
                you to refuse cookies, delete existing cookies, or set preferences for certain websites.
                Please note that disabling cookies may affect the functionality of our website and some
                features may not work as intended.
              </p>
              <p className="mt-4">
                For guidance on managing cookies in popular browsers:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Google Chrome: Settings → Privacy and Security → Cookies and other site data</li>
                <li>Mozilla Firefox: Settings → Privacy &amp; Security → Cookies and Site Data</li>
                <li>Safari: Preferences → Privacy → Manage Website Data</li>
                <li>Microsoft Edge: Settings → Cookies and site permissions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-3">6. Amendments</h2>
              <p>
                We reserve the right to modify this Cookie Policy at any time. Changes will be posted on
                this page with an updated date. We encourage you to review this page periodically.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-3">7. Contact Us</h2>
              <p>For any questions about our use of cookies, please contact us:</p>
              {contactBlock}
            </section>

          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CookiePolicy;